import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ProtectedRoute } from "./ProtectedRoute";
import { GuestRoute } from "./GuestRoute";
import { AdminProtectedRoute } from "./AdminProtectedRoute";
import { FacilitatorProtectedRoute } from "./FacilitatorProtectedRoute";
import { RouteFallback } from "./RouteFallback";

const AppLayout = lazy(() =>
  import("@/components/layout/AppLayout").then((m) => ({ default: m.AppLayout }))
);
const LandingLayout = lazy(() => import("@/components/layout/LandingPageLayout"));
const AdminLayout = lazy(() =>
  import("@/components/layout/AdminLayout").then((m) => ({ default: m.AdminLayout }))
);
const FacilitatorLayout = lazy(() =>
  import("@/components/layout/FacilitatorLayout").then((m) => ({ default: m.FacilitatorLayout }))
);

const DiscoverPage = lazy(() => import("@/pages/discover/DiscoverPage"));
const SectorDiscoverPage = lazy(() => import("@/pages/home/SectorDiscoverPage"));
const LoginPage = lazy(() => import("@/pages/auth/LoginPage"));
const LoginTwoFactorPage = lazy(() => import("@/two-factor/pages/LoginTwoFactorPage"));
const SignupPage = lazy(() => import("@/pages/signup/SignupPage"));
const SignupDocumentsPage = lazy(() => import("@/pages/signup/SignupDocumentsPage"));
const RegistrationSuccessPage = lazy(() => import("@/pages/signup/RegistrationSuccessPage"));
const ForgotPassword = lazy(() => import("@/pages/forgot-password/ForgotPassword"));
const ResendEmail = lazy(() => import("@/pages/resend-email/ResendEmail"));
const ResetPassword = lazy(() => import("@/pages/reset-password/ResetPassword"));
const ActivateAccountPage = lazy(() => import("@/pages/activate-account/ActivateAccountPage"));

const Maintenance = lazy(() => import("@/pages/maintenance/Maintenance"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

const DiscoverCompanyProfileGate = lazy(() =>
  import("@/routes/DiscoverCompanyProfileGate").then((m) => ({ default: m.DiscoverCompanyProfileGate }))
);
const AnonymousProfile = lazy(() => import("@/components/shared/AnonymousCompanyProfile/AnonymousProfile"));
const LoggedInCompanyWorkspaceRedirect = lazy(() => import("@/routes/LoggedInCompanyWorkspaceRedirect"));

const DashboardPage = lazy(() => import("@/pages/dashboard/DashboardPage"));
const CreateProfilePage = lazy(() => import("@/pages/create-profile/CreateProfilePage"));
const MyProfilePage = lazy(() => import("@/pages/profile/MyProfilePage"));
const EditProfile = lazy(() => import("@/pages/edit-profile/EditProfile"));
const SettingsPage = lazy(() => import("@/pages/mysupport/SettingsPage"));
const EnableTwoFactorPage = lazy(() => import("@/two-factor/pages/EnableTwoFactorPage"));
const InterestsPage = lazy(() => import("@/pages/interests/InterestsPage"));
const MatchSuccessPage = lazy(() => import("@/pages/interests/components/ItsaMatchPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/interests/components/PaymentSuccessPage"));
const CollaborationsPage = lazy(() => import("@/pages/collaborations/CollaborationsPage"));
const CollaborationWorkspacePage = lazy(() => import("@/pages/collaborations/CollaborationWorkspacePage"));
const CollaborationCompanyWorkspacePage = lazy(
  () => import("@/pages/collaborations/CollaborationCompanyWorkspacePage")
);
const MatchedProfileView = lazy(() => import("@/pages/profile/MatchedView"));
const ShortlistPage = lazy(() => import("@/pages/shortlist/ShortlistPage"));
const CreditsPage = lazy(() => import("@/pages/credits/CreditsPage"));
const BuyCreditsPage = lazy(() => import("@/pages/credits/components/BuyCreditsPage"));
const InvoicesPage = lazy(() => import("@/pages/invoices/InvoicesPage"));
const WalletPage = lazy(() => import("@/pages/invoices/WalletPage"));

const FacilitatorLoginPage = lazy(() => import("@/pages/facilitator/FacilitatorLoginPage"));
const FacilitatorDashboardPage = lazy(() => import("@/pages/facilitator/FacilitatorDashboardPage"));
const FacilitatorCompanyDetailPage = lazy(() => import("@/pages/facilitator/FacilitatorCompanyDetailPage"));
const FacilitatorRecommendPage = lazy(() => import("@/pages/facilitator/FacilitatorRecommendPage"));
const FacilitatorSendPreferredCompaniesPage = lazy(
  () => import("@/pages/facilitator/FacilitatorSendPreferredCompaniesPage")
);

const AdminLoginPage = lazy(() => import("@/pages/admin/AdminLoginPage"));
const AdminUsersPage = lazy(() => import("@/pages/admin/users/AdminUsersPage"));
const AdminFacilitatorsPage = lazy(() => import("@/pages/admin/users/AdminFacilitatorsPage"));
const AdminFacilitatorDetailPage = lazy(() => import("@/pages/admin/users/AdminFacilitatorDetailPage"));
const SubAdminManagementPage = lazy(() => import("@/pages/admin/users/SubAdminManagementPage"));
const AdminOuterUsersPage = lazy(() => import("@/pages/admin/outer-users/AdminOuterUsersPage"));
const AdminCreditAdjustmentHistoryPage = lazy(
  () => import("@/pages/admin/users/AdminCreditAdjustmentHistoryPage")
);
const AdminBulkMailHistoryPage = lazy(() => import("@/pages/admin/bulk-mail/AdminBulkMailHistoryPage"));
const AdminBulkMailPage = lazy(() => import("@/pages/admin/bulk-mail/AdminBulkMailPage"));
const AdminCompanyOnboardingChartPage = lazy(
  () => import("@/pages/admin/company-lists/AdminCompanyOnboardingChartPage")
);
const AdminCountriesMapPage = lazy(() => import("@/pages/admin/company-lists/AdminCountriesMapPage"));
const AdminDashboardPage = lazy(() => import("@/pages/admin/company-lists/AdminDashboardPage"));
const AdminPendingVerificationPage = lazy(
  () => import("@/pages/admin/company-lists/AdminPendingVerificationPage")
);
const AdminIndirectVerificationPage = lazy(
  () => import("@/pages/admin/indirect-verification/AdminIndirectVerificationPage")
);
const AdminMeilisearchReindexPage = lazy(() => import("@/pages/admin/meilisearch/AdminMeilisearchReindexPage"));
const AdminCollaborationTable = lazy(() => import("@/pages/admin/collaboration-lists/AdminCollaborationTable"));
const AdminInterestManagement = lazy(() => import("@/pages/admin/interest-management/InterestManagement"));
const AdminPartnerMatchPage = lazy(() => import("@/pages/admin/partner-match/AdminPartnerMatchPage"));
const AdminExpressInterestPreviewPage = lazy(
  () => import("@/pages/admin/express-interest/AdminExpressInterestPreviewPage")
);
const AdminDiscoverTable = lazy(() => import("@/pages/admin/discover-listings/AdminDiscoverTable"));
const AdminGuidancePage = lazy(() => import("@/pages/admin/guidance-table/AdminGuidancePage"));
const AdminCompanyDetailView = lazy(() => import("@/pages/admin/company-lists/components/AdminMatchedView"));
const AdminCompanyFullEditPage = lazy(() => import("@/pages/admin/company-lists/AdminCompanyFullEditPage"));
const AdminCollaborationDetailPage = lazy(
  () => import("@/pages/admin/collaboration-lists/components/WorkspaceView")
);
function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public routes */}
        <Route element={<GuestRoute />}>
          <Route element={<LandingLayout />}>
            <Route path="/" element={<DiscoverPage />} />
            <Route path="/sector/:slug" element={<SectorDiscoverPage />} />
            <Route path="/discover" element={<DiscoverPage />} />
          </Route>
        </Route>
        {/* Admin/shared discover company URL — must work for guests and logged-in clients */}
        <Route path="/discover/:id" element={<DiscoverCompanyProfileGate />}>
          <Route element={<LandingLayout />}>
            <Route index element={<AnonymousProfile pageID="DISCOVER_PUBLIC" />} />
          </Route>
        </Route>
        <Route path="/registration-success" element={<RegistrationSuccessPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/2fa" element={<LoginTwoFactorPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/signup/company-details" element={<Navigate to="/login" replace />} />
        <Route path="/signup/documents" element={<SignupDocumentsPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/resend-email" element={<ResendEmail />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/activate-account" element={<ActivateAccountPage />} />
        <Route path="/facilitator/login" element={<FacilitatorLoginPage />} />
        <Route path="/facilitator/signup" element={<FacilitatorLoginPage />} />

        {/* Protected routes with AppShell layout */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/create-profile" element={<CreateProfilePage />} />
          <Route path="/profile" element={<MyProfilePage />} />
          <Route path="/my-discovery" element={<DiscoverPage />} />
          <Route path="/my-discovery/:id" element={<LoggedInCompanyWorkspaceRedirect />} />
          <Route path="/requests" element={<InterestsPage />} />
          <Route path="/collaborations" element={<CollaborationsPage />} />
          <Route path="/shortlist" element={<ShortlistPage />} />
          <Route path="/shortlist/:id" element={<LoggedInCompanyWorkspaceRedirect />} />
          <Route path="/credits" element={<CreditsPage />} />
          <Route path="/credits/buy" element={<BuyCreditsPage />} />
          <Route path="/invoices" element={<InvoicesPage />} />
          <Route path="/invoices/wallet" element={<WalletPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/two-factor/setup" element={<EnableTwoFactorPage />} />
          <Route path="/profile/edit" element={<EditProfile />} />
          <Route path="/requests/:id" element={<LoggedInCompanyWorkspaceRedirect />} />
          <Route path="/requests/match-success/:id" element={<MatchSuccessPage />} />
          <Route path="/requests/payment-success/:id" element={<PaymentSuccessPage />} />

          <Route path="/collaborations/company/:companyId" element={<CollaborationCompanyWorkspacePage />} />
          <Route path="/collaborations/:id" element={<CollaborationWorkspacePage />} />
          <Route path="/collaborations/:id/profile" element={<MatchedProfileView />} />
        </Route>

        {/* ============ Admin routes ============ */}
        <Route path="/admin/login" element={<AdminLoginPage />} />

        <Route
          element={
            <AdminProtectedRoute>
              <AdminLayout />
            </AdminProtectedRoute>
          }
        >
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/facilitators" element={<AdminFacilitatorsPage />} />
          <Route path="/admin/facilitators/:id" element={<AdminFacilitatorDetailPage />} />
          <Route path="/admin/sub-admins" element={<SubAdminManagementPage />} />
          <Route path="/admin/outer-users" element={<AdminOuterUsersPage />} />
          <Route path="/admin/credit-history" element={<AdminCreditAdjustmentHistoryPage />} />
          <Route path="/admin/bulk-mail/history" element={<AdminBulkMailHistoryPage />} />
          <Route path="/admin/bulk-mail" element={<AdminBulkMailPage />} />
          <Route path="/admin/company-onboarding" element={<AdminCompanyOnboardingChartPage />} />
          <Route path="/admin/countries-map" element={<AdminCountriesMapPage />} />
          <Route path="/admin/companies" element={<AdminDashboardPage />} />
          <Route path="/admin/pending-verification" element={<AdminPendingVerificationPage />} />
          <Route path="/admin/indirect-verification" element={<AdminIndirectVerificationPage />} />
          <Route path="/admin/meilisearch" element={<AdminMeilisearchReindexPage />} />
          <Route path="/admin/collaborations" element={<AdminCollaborationTable />} />
          <Route path="/admin/interest" element={<AdminInterestManagement />} />
          <Route path="/admin/partner-match" element={<AdminPartnerMatchPage />} />
          <Route path="/admin/express-interest-preview" element={<AdminExpressInterestPreviewPage />} />
          <Route path="/admin/discover" element={<AdminDiscoverTable />} />
          <Route path="/admin/resource" element={<AdminGuidancePage />} />
          <Route path="/admin/companies/:id" element={<AdminCompanyDetailView />} />
          <Route path="/admin/companies/:id/edit" element={<AdminCompanyFullEditPage />} />
          <Route path="/admin/collaborations/:id" element={<AdminCollaborationDetailPage />} />
          <Route path="/admin/two-factor" element={<EnableTwoFactorPage />} />
        </Route>

        {/* ============ Facilitator routes ============ */}
        <Route
          element={
            <FacilitatorProtectedRoute>
              <FacilitatorLayout />
            </FacilitatorProtectedRoute>
          }
        >
          <Route path="/facilitator/dashboard" element={<FacilitatorDashboardPage />} />
          <Route path="/facilitator/managed" element={<FacilitatorDashboardPage />} />
          <Route path="/facilitator/send-preferred-companies" element={<FacilitatorSendPreferredCompaniesPage />} />
          <Route path="/facilitator/companies/:id" element={<FacilitatorCompanyDetailPage />} />
          <Route path="/facilitator/companies/:id/recommend" element={<FacilitatorRecommendPage />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
        <Route path="/maintenance" element={<Maintenance />} />
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
